import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Building2, Radio } from "lucide-react";
import { Link } from "react-router-dom";
import Logo from "./Logo";

export default function Hero({ onJoinWaitlist, onOwnerClick, onMunicipalClick }) {
  return (
    <section className="relative overflow-hidden">
      <div className="gv-grid-bg absolute inset-0" aria-hidden />
      <div className="gv-noise" aria-hidden />

      {/* Top nav */}
      <header className="relative z-10 mx-auto flex max-w-7xl items-center justify-between px-6 py-6 lg:px-8">
        <Link to="/" className="inline-flex">
          <Logo size={32} />
        </Link>
        <nav className="hidden items-center gap-8 md:flex">
          <Link
            to="/about"
            className="font-body text-sm text-[#94A3B8] transition-colors hover:text-[#F8FAFC]"
            data-testid="nav-about"
          >
            About
          </Link>
          <Link
            to="/pilot"
            className="font-body text-sm text-[#94A3B8] transition-colors hover:text-[#F8FAFC]"
            data-testid="nav-pilot"
          >
            Pilot
          </Link>
          <a
            href="#who-we-serve"
            className="font-body text-sm text-[#94A3B8] transition-colors hover:text-[#F8FAFC]"
            data-testid="nav-owners"
          >
            For Owners
          </a>
          <Link
            to="/contact"
            className="font-body text-sm text-[#94A3B8] transition-colors hover:text-[#F8FAFC]"
            data-testid="nav-contact"
          >
            Contact
          </Link>
        </nav>
        <Link
          to="/walkthrough"
          className="hidden items-center gap-1.5 rounded-md bg-[#22C55E] px-4 py-2 font-body text-sm font-semibold text-[#07111F] transition-colors hover:bg-[#22C55E]/90 md:inline-flex focus:outline-none focus-visible:ring-2 focus-visible:ring-[#22C55E]/40"
          data-testid="nav-cta-walkthrough"
        >
          Request Private Walkthrough
        </Link>
      </header>

      {/* Hero body */}
      <div id="top" className="relative z-10 mx-auto max-w-7xl px-6 pb-24 pt-16 lg:px-8 lg:pt-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto max-w-4xl text-center"
        >
          <p
            className="mb-6 font-display text-[12px] font-extrabold uppercase tracking-[0.18em] text-[#38BDF8]"
            data-testid="coming-soon-badge"
          >
            Coming Soon
          </p>

          <h1
            className="font-display text-[44px] font-extrabold leading-[0.98] tracking-[-0.05em] text-[#F8FAFC] sm:text-6xl lg:text-[86px]"
            data-testid="hero-title"
          >
            Infrastructure ownership
            <br className="hidden sm:block" />
            <span className="text-[#94A3B8]"> is evolving.</span>
          </h1>

          <p
            className="mx-auto mt-7 max-w-2xl font-body text-base leading-relaxed text-[#CBD5E1] sm:text-lg"
            data-testid="hero-subtitle"
          >
            InfraYield is building a dedicated platform for infrastructure ownership
            intelligence across telecom, billboard, and digital infrastructure assets.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={onJoinWaitlist}
              className="group inline-flex items-center gap-2 rounded-md bg-[#22C55E] px-6 py-3.5 font-body text-sm font-semibold text-[#07111F] transition-all hover:bg-[#22C55E]/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#22C55E]/40"
              data-testid="hero-cta-join"
            >
              Join Early Access
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <button
              onClick={onOwnerClick}
              className="inline-flex items-center gap-2 rounded-md border border-[#334155] bg-transparent px-6 py-3.5 font-body text-sm font-semibold text-[#F8FAFC] transition-colors hover:border-[#38BDF8]/60 hover:bg-[#0F1B2D] focus:outline-none focus-visible:border-[#38BDF8] focus-visible:ring-2 focus-visible:ring-[#38BDF8]/30"
              data-testid="hero-cta-owner"
            >
              Infrastructure Owners
            </button>
            <button
              onClick={onMunicipalClick}
              className="inline-flex items-center gap-2 rounded-md border border-[#334155] bg-transparent px-6 py-3.5 font-body text-sm font-semibold text-[#F8FAFC] transition-colors hover:border-[#38BDF8]/60 hover:bg-[#0F1B2D] focus:outline-none focus-visible:border-[#38BDF8] focus-visible:ring-2 focus-visible:ring-[#38BDF8]/30"
              data-testid="hero-cta-municipal"
            >
              Municipal Partnerships
            </button>
          </div>

          <p
            className="mx-auto mt-8 max-w-2xl font-body text-xs leading-relaxed text-[#64748B]"
            data-testid="hero-disclaimer"
          >
            Investment opportunities, if offered, will only be made through compliant
            securities structures and official offering documents.
          </p>

          <div
            className="mx-auto mt-12 flex flex-wrap items-center justify-center gap-3"
            data-testid="hero-social-proof"
          >
            <div className="flex -space-x-2">
              {["#22C55E", "#38BDF8", "#1E293B", "#0F1B2D"].map((bg, i) => (
                <span
                  key={i}
                  className="inline-block h-7 w-7 rounded-full border border-[#1E293B] ring-2 ring-[#07111F]"
                  style={{ background: bg }}
                />
              ))}
            </div>
            <p className="font-body text-sm text-[#CBD5E1]">
              <span className="font-display font-semibold text-[#F8FAFC]">
                Our traction is growing.
              </span>{" "}
              <span className="text-[#94A3B8]">
                <span className="font-semibold text-[#F8FAFC]">500+</span> people on the waitlist
              </span>
            </p>
          </div>
        </motion.div>

        {/* Stat row */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-20 grid grid-cols-1 divide-y divide-[#1E293B] border-y border-[#1E293B] sm:grid-cols-3 sm:divide-x sm:divide-y-0"
        >
          {[
            { icon: Radio, label: "Telecom Infrastructure", note: "Cell towers & rooftop leases" },
            { icon: Building2, label: "Data Center Sites", note: "Edge & enterprise" },
            { icon: ShieldCheck, label: "Municipal Partners", note: "Public assets & broadband" },
          ].map((s, i) => (
            <div key={i} className="flex items-center gap-3 px-6 py-6">
              <div className="flex h-9 w-9 items-center justify-center rounded-md border border-[#1E293B] bg-[#0F1B2D] text-[#38BDF8]">
                <s.icon className="h-4 w-4" />
              </div>
              <div className="text-left">
                <p className="font-display text-sm font-semibold text-[#F8FAFC]">
                  {s.label}
                </p>
                <p className="font-body text-xs text-[#94A3B8]">{s.note}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
